import React, { useState, useMemo, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  Globe,
  Server,
  Shield,
  Clock,
  Activity,
  FileText,
  Code2,
  Hash,
  ArrowRight,
  Search,
  Zap,
  Database,
  Network,
  ArrowRightLeft,
  Tag,
  RefreshCw,
  AlertCircle,
  Braces,
  Ban,
} from 'lucide-react'
import { api, type TopologyRoute } from '@services/api'

// ─── Types (re-exported from api.ts for local use) ───────────────────────

type RouteCategory = 'internal' | 'external' | 'auth' | 'health'
type KongPlugin = TopologyRoute['plugins'][number]
type KongRouteEntry = TopologyRoute

// (static ROUTES and plugin arrays removed — data now comes from the Kong Admin API)

// ─── Plugin badge config ───────────────────────────────────────────────────

const PLUGIN_META: Record<
  string,
  { icon: React.ElementType; bgClass: string; textClass: string; borderClass: string; label: string }
> = {
  'rate-limiting': {
    icon: Clock,
    bgClass: 'bg-amber-50 dark:bg-amber-900/30',
    textClass: 'text-amber-700 dark:text-amber-300',
    borderClass: 'border-amber-200 dark:border-amber-800/50',
    label: 'Rate Limit',
  },
  'correlation-id': {
    icon: Hash,
    bgClass: 'bg-blue-50 dark:bg-blue-900/30',
    textClass: 'text-blue-700 dark:text-blue-300',
    borderClass: 'border-blue-200 dark:border-blue-800/50',
    label: 'Correlation ID',
  },
  'request-transformer': {
    icon: Code2,
    bgClass: 'bg-purple-50 dark:bg-purple-900/30',
    textClass: 'text-purple-700 dark:text-purple-300',
    borderClass: 'border-purple-200 dark:border-purple-800/50',
    label: 'Req Transform',
  },
  'response-transformer': {
    icon: ArrowRightLeft,
    bgClass: 'bg-teal-50 dark:bg-teal-900/30',
    textClass: 'text-teal-700 dark:text-teal-300',
    borderClass: 'border-teal-200 dark:border-teal-800/50',
    label: 'Res Transform',
  },
  'file-log': {
    icon: FileText,
    bgClass: 'bg-slate-50 dark:bg-white/5',
    textClass: 'text-slate-600 dark:text-gray-400',
    borderClass: 'border-slate-200 dark:border-gray-700',
    label: 'File Log',
  },
  prometheus: {
    icon: Activity,
    bgClass: 'bg-rose-50 dark:bg-rose-900/30',
    textClass: 'text-rose-700 dark:text-rose-300',
    borderClass: 'border-rose-200 dark:border-rose-800/50',
    label: 'Prometheus',
  },
  'json-transformer': {
    icon: Braces,
    bgClass: 'bg-cyan-50 dark:bg-cyan-900/30',
    textClass: 'text-cyan-700 dark:text-cyan-300',
    borderClass: 'border-cyan-200 dark:border-cyan-800/50',
    label: 'JSON Transform',
  },
  'request-termination': {
    icon: Ban,
    bgClass: 'bg-red-50 dark:bg-red-900/30',
    textClass: 'text-red-700 dark:text-red-300',
    borderClass: 'border-red-200 dark:border-red-800/50',
    label: 'Req Terminate',
  },
}

// ─── Category config ───────────────────────────────────────────────────────

const CATEGORY_META: Record<
  RouteCategory,
  { label: string; bgClass: string; textClass: string; borderClass: string; dotClass: string }
> = {
  internal: {
    label: 'Internal',
    bgClass: 'bg-indigo-50 dark:bg-indigo-900/30',
    textClass: 'text-indigo-700 dark:text-indigo-300',
    borderClass: 'border-indigo-200 dark:border-indigo-800/50',
    dotClass: 'bg-indigo-500',
  },
  external: {
    label: 'External',
    bgClass: 'bg-emerald-50 dark:bg-emerald-900/30',
    textClass: 'text-emerald-700 dark:text-emerald-300',
    borderClass: 'border-emerald-200 dark:border-emerald-800/50',
    dotClass: 'bg-emerald-500',
  },
  auth: {
    label: 'Auth',
    bgClass: 'bg-orange-50 dark:bg-orange-900/30',
    textClass: 'text-orange-700 dark:text-orange-300',
    borderClass: 'border-orange-200 dark:border-orange-800/50',
    dotClass: 'bg-orange-500',
  },
  health: {
    label: 'Health',
    bgClass: 'bg-gray-50 dark:bg-white/5',
    textClass: 'text-gray-600 dark:text-gray-400',
    borderClass: 'border-gray-200 dark:border-gray-700',
    dotClass: 'bg-gray-400 dark:bg-gray-500',
  },
}

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300',
  POST: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  PUT: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
  PATCH: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
  DELETE: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
}

// ─── Sub-components ────────────────────────────────────────────────────────

const PluginBadge: React.FC<{ plugin: KongPlugin }> = ({ plugin }) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 })
  const badgeRef = useRef<HTMLSpanElement>(null)
  const meta = PLUGIN_META[plugin.name]

  useEffect(() => {
    if (showTooltip && badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect()
      setTooltipPos({
        top: rect.top + window.scrollY - 8,
        left: rect.left + window.scrollX + rect.width / 2,
      })
    }
  }, [showTooltip])

  if (!meta) {
    return (
      <span className="inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-600 dark:border-gray-700 dark:bg-white/5 dark:text-gray-400">
        <Zap className="h-3 w-3" />
        {plugin.name}
      </span>
    )
  }

  const Icon = meta.icon
  const scopeDot =
    plugin.scope === 'global'
      ? 'bg-slate-400 dark:bg-gray-500'
      : plugin.scope === 'service'
        ? 'bg-indigo-400'
        : 'bg-orange-400'

  const tooltip = showTooltip
    ? createPortal(
        <div
          style={{
            position: 'absolute',
            top: tooltipPos.top,
            left: tooltipPos.left,
            transform: 'translate(-50%, -100%)',
            zIndex: 9999,
          }}
          className="w-max max-w-55 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-xl dark:border-gray-700 dark:bg-gray-800 dark:shadow-none"
        >
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">{plugin.name}</p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{plugin.summary}</p>
          <p className="mt-1 text-xs text-gray-400 capitalize dark:text-gray-500">scope: {plugin.scope}</p>
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-200" />
        </div>,
        document.body,
      )
    : null

  return (
    <>
      <span
        ref={badgeRef}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`inline-flex cursor-default items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium transition-opacity ${meta.bgClass} ${meta.textClass} ${meta.borderClass}`}
      >
        <Icon className="h-3 w-3 shrink-0" />
        {meta.label}
        <span className={`ml-0.5 h-1.5 w-1.5 rounded-full ${scopeDot}`} title={`scope: ${plugin.scope}`} />
      </span>
      {tooltip}
    </>
  )
}

const FlowConnector: React.FC = () => (
  <div className="flex min-w-4 flex-1 items-center">
    <div className="h-px flex-1 bg-gray-300 dark:bg-gray-600" />
    <ArrowRight className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500" />
    <div className="h-px flex-1 bg-gray-300 dark:bg-gray-600" />
  </div>
)

const RouteCard: React.FC<{ route: KongRouteEntry }> = ({ route }) => {
  const catMeta = CATEGORY_META[route.category]
  const isExternal = route.category === 'external'

  return (
    <div
      className={`overflow-hidden rounded-xl border bg-white shadow-sm transition-shadow hover:shadow-md dark:bg-gray-800 dark:shadow-none dark:hover:shadow-none ${catMeta.borderClass}`}
    >
      {/* Card header */}
      <div
        className={`flex items-center justify-between border-b px-4 py-2.5 ${catMeta.bgClass} ${catMeta.borderClass}`}
      >
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${catMeta.dotClass}`} />
          <span className={`text-xs font-semibold tracking-wide uppercase ${catMeta.textClass}`}>{catMeta.label}</span>
          <span className="font-mono text-sm font-medium text-gray-700 dark:text-gray-300">{route.routeName}</span>
        </div>
        <div className="flex items-center gap-1">
          {route.tags.map((t) => (
            <span
              key={t}
              className="rounded bg-white px-1.5 py-0.5 text-xs text-gray-500 dark:bg-gray-800/60 dark:text-gray-400"
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Flow row */}
      <div className="flex items-stretch overflow-x-auto p-3">
        {/* ── Node 1: Consumer ── */}
        <div className="flex w-36 shrink-0 flex-col items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 dark:border-gray-700 dark:bg-white/5">
          <Globe className="h-5 w-5 text-slate-500 dark:text-gray-400" />
          <span className="mt-1.5 text-center text-xs leading-tight font-medium text-slate-500 dark:text-gray-400">
            Client
          </span>
          <div className="mt-1.5 flex flex-wrap justify-center gap-1">
            {route.protocols.map((p) => (
              <span
                key={p}
                className="rounded bg-slate-200 px-1 py-0.5 font-mono text-xs text-slate-600 uppercase dark:bg-gray-700 dark:text-gray-400"
              >
                {p}
              </span>
            ))}
          </div>
        </div>

        <FlowConnector />

        {/* ── Node 2: Kong Endpoint ── */}
        <div className="flex w-80 shrink-0 flex-col justify-center overflow-hidden rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-3 dark:border-indigo-800/50 dark:bg-indigo-900/30">
          <div className="mb-1 flex items-center gap-1.5">
            <Network className="h-4 w-4 shrink-0 text-indigo-500" />
            <span className="text-xs font-semibold tracking-wide text-indigo-600 uppercase">Kong Endpoint</span>
          </div>
          {route.paths.map((p) => (
            <code key={p} className="font-mono text-sm font-medium break-all text-indigo-800 dark:text-indigo-300">
              {p}
            </code>
          ))}
          {route.headerMatch && (
            <div className="mt-1.5 flex items-center gap-1 rounded bg-indigo-100 px-2 py-1 dark:bg-indigo-900/40">
              <Tag className="h-3 w-3 shrink-0 text-indigo-500" />
              <span className="font-mono text-xs break-all text-indigo-700 dark:text-indigo-300">
                {route.headerMatch.header}: {route.headerMatch.value}
              </span>
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-1">
            {route.methods.map((m) => (
              <span
                key={m}
                className={`rounded px-1.5 py-0.5 text-xs font-semibold ${METHOD_COLORS[m] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}
              >
                {m}
              </span>
            ))}
          </div>
          {route.stripPath && <span className="mt-1.5 text-xs text-indigo-500">strip_path: true</span>}
        </div>

        <FlowConnector />

        {/* ── Node 3: Active Plugins ── */}
        <div className="flex w-80 shrink-0 flex-col justify-center overflow-hidden rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 dark:border-amber-800/50 dark:bg-amber-900/30">
          <div className="mb-2 flex items-center gap-1.5">
            <Shield className="h-4 w-4 shrink-0 text-amber-600" />
            <span className="text-xs font-semibold tracking-wide text-amber-700 uppercase dark:text-amber-300">
              Active Plugins ({route.plugins.length})
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {route.plugins.map((plugin) => (
              <PluginBadge key={`${route.id}-${plugin.name}`} plugin={plugin} />
            ))}
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs text-amber-600/70">
            <span className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-400 dark:bg-gray-500" />
              global
            </span>
            <span className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-400" />
              service
            </span>
            <span className="flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-orange-400" />
              route
            </span>
          </div>
        </div>

        <FlowConnector />

        {/* ── Node 4: Upstream ── */}
        <div
          className={`flex w-60 shrink-0 flex-col justify-center overflow-hidden rounded-lg border px-3 py-3 ${
            isExternal
              ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-800/50 dark:bg-emerald-900/30'
              : 'border-violet-200 bg-violet-50 dark:border-violet-800/50 dark:bg-violet-900/30'
          }`}
        >
          <div className="mb-1 flex items-center gap-1.5">
            {isExternal ? (
              <Globe className="h-4 w-4 shrink-0 text-emerald-600" />
            ) : (
              <Server className="h-4 w-4 shrink-0 text-violet-600" />
            )}
            <span
              className={`text-xs font-semibold tracking-wide uppercase ${
                isExternal ? 'text-emerald-600' : 'text-violet-600'
              }`}
            >
              {isExternal ? 'External Upstream' : 'Internal Upstream'}
            </span>
          </div>
          <span
            className={`text-sm font-semibold ${isExternal ? 'text-emerald-800 dark:text-emerald-300' : 'text-violet-800 dark:text-violet-300'}`}
          >
            {route.service}
          </span>
          <code className={`mt-1 font-mono text-xs break-all ${isExternal ? 'text-emerald-600' : 'text-violet-600'}`}>
            {route.upstreamUrl}
          </code>
          {!isExternal && (
            <div className="mt-1.5 flex items-center gap-1">
              <Database className="h-3 w-3 text-violet-400" />
              <span className="text-xs text-violet-500">round-robin LB • health checks</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Main page ─────────────────────────────────────────────────────────────

type FilterCategory = 'all' | RouteCategory

const FILTER_TABS: { key: FilterCategory; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'internal', label: 'Internal' },
  { key: 'external', label: 'External' },
  { key: 'auth', label: 'Auth' },
  { key: 'health', label: 'Health' },
]

const KongTopology: React.FC = () => {
  const [routes, setRoutes] = useState<KongRouteEntry[]>([])
  const [fetchedAt, setFetchedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<FilterCategory>('all')
  const [pluginFilter, setPluginFilter] = useState<string>('all')

  const loadTopology = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await api.adminGetKongTopology()
      setRoutes(data.routes)
      setFetchedAt(data.fetchedAt)
    } catch {
      setError('Failed to load Kong topology. Is the Kong Admin API reachable?')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadTopology()
  }, [])

  const allPluginNames = useMemo(() => {
    const names = new Set<string>()
    routes.forEach((r) => r.plugins.forEach((p) => names.add(p.name)))
    return Array.from(names)
  }, [routes])

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    return routes.filter((r) => {
      const matchesCategory = categoryFilter === 'all' || r.category === categoryFilter
      const matchesPlugin = pluginFilter === 'all' || r.plugins.some((p) => p.name === pluginFilter)
      const matchesSearch =
        !q ||
        r.routeName.toLowerCase().includes(q) ||
        r.paths.some((p) => p.toLowerCase().includes(q)) ||
        r.service.toLowerCase().includes(q) ||
        r.upstreamUrl.toLowerCase().includes(q) ||
        r.tags.some((t) => t.toLowerCase().includes(q)) ||
        (r.headerMatch?.value.toLowerCase().includes(q) ?? false)
      return matchesCategory && matchesPlugin && matchesSearch
    })
  }, [routes, search, categoryFilter, pluginFilter])

  const stats = useMemo(
    () => ({
      total: routes.length,
      external: routes.filter((r) => r.category === 'external').length,
      internal: routes.filter((r) => r.category === 'internal').length,
      uniquePlugins: allPluginNames.length,
    }),
    [routes, allPluginNames],
  )

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 dark:bg-indigo-900/40">
            <Network className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Kong Topology</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              API Gateway routing — services · plugins · upstreams
              {fetchedAt && (
                <span className="ml-2 text-gray-400 dark:text-gray-500">
                  · fetched {new Date(fetchedAt).toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={() => void loadTopology()}
          disabled={loading}
          className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-900"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800/50 dark:bg-red-900/30">
          <AlertCircle className="h-5 w-5 shrink-0 text-red-500" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && routes.length === 0 && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-32 animate-pulse rounded-xl border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-700"
            />
          ))}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* ── Stats ── */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              {
                label: 'Total Routes',
                value: stats.total,
                icon: Network,
                color: 'text-indigo-600',
                bg: 'bg-indigo-50 dark:bg-indigo-900/30',
              },
              {
                label: 'Internal Routes',
                value: stats.internal,
                icon: Server,
                color: 'text-violet-600',
                bg: 'bg-violet-50 dark:bg-violet-900/30',
              },
              {
                label: 'External Upstreams',
                value: stats.external,
                icon: Globe,
                color: 'text-emerald-600',
                bg: 'bg-emerald-50 dark:bg-emerald-900/30',
              },
              {
                label: 'Active Plugin Types',
                value: stats.uniquePlugins,
                icon: Shield,
                color: 'text-amber-600',
                bg: 'bg-amber-50 dark:bg-amber-900/30',
              },
            ].map((s) => {
              const Icon = s.icon
              return (
                <div key={s.label} className="card flex items-center gap-4 p-4">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${s.bg}`}>
                    <Icon className={`h-5 w-5 ${s.color}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{s.value}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{s.label}</p>
                  </div>
                </div>
              )
            })}
          </div>

          {/* ── Search + Filters ── */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search routes, paths, services…"
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pr-4 pl-9 text-sm text-gray-700 placeholder-gray-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
              />
            </div>

            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border border-gray-200 bg-white p-0.5 dark:border-gray-700 dark:bg-gray-800">
                {FILTER_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setCategoryFilter(tab.key)}
                    className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                      categoryFilter === tab.key
                        ? 'bg-indigo-600 text-white shadow-sm dark:shadow-none'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <select
                value={pluginFilter}
                onChange={(e) => setPluginFilter(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-700 focus:border-indigo-400 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
              >
                <option value="all">All plugins</option>
                {allPluginNames.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Legend ── */}
          <div className="flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-xs text-gray-500 dark:border-gray-700 dark:bg-white/5 dark:text-gray-400">
            <span className="font-semibold text-gray-600 dark:text-gray-400">Flow:</span>
            <span className="flex items-center gap-1.5">
              <Globe className="h-3.5 w-3.5 text-slate-500 dark:text-gray-400" /> Client
            </span>
            <ArrowRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
            <span className="flex items-center gap-1.5">
              <Network className="h-3.5 w-3.5 text-indigo-500" /> Kong Endpoint
            </span>
            <ArrowRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
            <span className="flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5 text-amber-500" /> Active Plugins
            </span>
            <ArrowRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
            <span className="flex items-center gap-1.5">
              <Server className="h-3.5 w-3.5 text-violet-500" /> Upstream
            </span>
            <span className="ml-auto text-gray-400 dark:text-gray-500">
              Hover plugin badges for details · coloured dot = scope
            </span>
          </div>

          {/* ── Route cards ── */}
          {filtered.length === 0 ? (
            <div className="card py-16 text-center">
              <Network className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
              <p className="mt-3 text-sm font-medium text-gray-500 dark:text-gray-400">No routes match your filters.</p>
              <button
                onClick={() => {
                  setSearch('')
                  setCategoryFilter('all')
                  setPluginFilter('all')
                }}
                className="mt-3 text-sm text-indigo-600 hover:underline"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Showing {filtered.length} of {routes.length} routes
              </p>
              {filtered.map((route) => (
                <RouteCard key={route.id} route={route} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default KongTopology
